import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { ArrowLeft, FileDown, Copy, Trash2, ShieldCheck } from "lucide-react";
import DocumentEditor from "../components/DocumentEditor";
import SendEmailDialog from "../components/SendEmailDialog";

const STATUSES = ["pending", "paid", "partial", "overdue", "cancelled"];

export default function InvoiceEditor() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new";
  const [existing, setExisting] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    if (!isNew) {
      api.get(`/invoices/${id}`).then((r) => {
        setExisting(r.data);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [id, isNew]);

  const onSaved = (doc) => {
    if (isNew) navigate(`/app/invoices/${doc.id}`, { replace: true });
    else setExisting(doc);
  };

  const downloadPdf = () => {
    if (!existing) return;
    const token = localStorage.getItem("hosys_token");
    const url = `${API}/invoices/${id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener");
  };

  const duplicate = async () => {
    const r = await api.post(`/invoices/${id}/duplicate`);
    navigate(`/app/invoices/${r.data.id}`);
  };

  const remove = async () => {
    if (!window.confirm("Eliminar?")) return;
    await api.delete(`/invoices/${id}`);
    navigate("/app/invoices");
  };

  if (loading) return <div className="text-slate-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="invoice-editor-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/app/invoices" className="text-slate-400 hover:text-white shrink-0">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tighter truncate">
            {isNew ? t("editor.new_invoice") : `${t("editor.edit_invoice")} · ${existing?.number}`}
          </h1>
        </div>
        {!isNew && existing && (
          <div className="flex flex-wrap items-center gap-2">
            <SendEmailDialog
              kind="invoice"
              docId={existing.id}
              docNumber={existing.number}
              clientEmail={clients.find((c) => c.id === existing.client_id)?.email}
              clientName={clients.find((c) => c.id === existing.client_id)?.name}
            />
            <Button variant="outline" size="sm" onClick={downloadPdf} className="border-white/10" data-testid="invoice-download-pdf">
              <FileDown size={14} className="mr-1.5" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={duplicate} className="border-white/10">
              <Copy size={14} className="mr-1.5" /> Duplicar
            </Button>
            <Button variant="outline" size="sm" onClick={remove} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10">
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </div>

      <DocumentEditor
        kind="invoice"
        existing={existing}
        clients={clients}
        onSaved={(d) => { onSaved(d); setExisting(d); }}
        statusOptions={STATUSES}
      />

      {existing?.verifactu_hash && (
        <div
          className="card-surface p-5 border border-blue-500/30 bg-blue-500/5"
          data-testid="verifactu-card"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-blue-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-semibold text-blue-200">
                  VERI*FACTU
                </h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                  RD 1007/2023 · ACTIU
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Registre de facturació verificable a la seu electrònica de l'AEAT
                (seq #{existing.verifactu_seq}).
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs font-mono break-all">
                <div>
                  <span className="text-slate-500">Empremta SHA-256:</span>
                  <div className="text-slate-200">{existing.verifactu_hash}</div>
                </div>
                <div>
                  <span className="text-slate-500">Hash anterior:</span>
                  <div className="text-slate-300">
                    {existing.verifactu_prev_hash || "—"}
                  </div>
                </div>
              </div>
              <a
                href={existing.verifactu_qr_url}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-3 text-xs text-blue-300 hover:text-blue-200 underline"
                data-testid="verifactu-aeat-link"
              >
                Verificar a l'AEAT →
              </a>
            </div>
          </div>
        </div>
      )}

      {existing && (
        <div
          className="card-surface p-5 border border-amber-500/30 bg-amber-500/5"
          data-testid="facturae-card"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-amber-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-semibold text-amber-100">
                  Facturae 3.2.2 (factura electrònica)
                </h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300">
                  PREPARAT · DESACTIVAT
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Exportació XML segons l'esquema Facturae 3.2.2 (Ministeri d'Hisenda),
                preparada per quan la Llei Crea y Crece obligui a la factura
                electrònica B2B. Activable amb una sola variable d'entorn quan
                entri en vigor (caldrà afegir signatura XAdES amb certificat).
              </p>
              <div className="flex items-center gap-2 mt-3">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${existing.id}/facturae?preview=true`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-amber-300 hover:text-amber-200 underline"
                  data-testid="facturae-preview-link"
                >
                  Previsualitzar XML (no vàlid legalment) →
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
